import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import { useSelector } from 'react-redux';
import moment from 'moment';

import CardBody from 'components/Card/CardBody';
import {
	BodyL,
	BodyMBold,
	BodyS,
	BodySBold,
	DescriptionS,
	HeadingM,
	HeadingMBold,
} from 'components/Typography/Typography';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';

import { dynamicTranslate } from 'components/Translation';
import { getCommonLabels, getPaymentMethodLabels } from 'redux/modules/dynamicLabels/selectors';

import { makeStyles } from '@material-ui/core/styles';
import styles from 'assets/jss/material-dashboard-pro-react/components/paymentMethodsStyle';

import { roundup } from '../helper/number';
import { getLanguage } from 'utils/language/language';

import PaymentService from 'services/payments/payment';
import { BillItem, MonthlyPaymentItem, PaymentSummary } from '../type';

import securePayment from 'assets/img/cards/credit-card-v2.jpg';
import warning from 'assets/img/warning.svg';

// @ts-ignore
const useStyles = makeStyles(styles);

interface OrderSummaryProps {
	bills: BillItem[];
}

const OrderSummary = ({ bills }: OrderSummaryProps) => {
	const lang = getLanguage();
	const classes = useStyles();
	const commonLabels = useSelector(getCommonLabels);
	const paymentMethodLabels = useSelector(getPaymentMethodLabels);

	const [hasError, setError] = useState<boolean>(false);
	const [totalAmount, setTotalAmount] = useState<string>('');
	const [orderItems, setOrderItems] = useState<MonthlyPaymentItem[]>([]);

	useEffect(() => {
		const billsItems = bills?.reduce((state, bill) => {
			if (!bill?.accountId) return state;

			state[bill.accountId] = [...(state[bill.accountId] || []), bill.code];
			return state;
		}, {} as Record<string, string[]>);

		const payload = {
			paymentSummaryItems: Object.entries(billsItems || {}).map(([billingId, billNumbers]) => ({
				billingId,
				billNumbers,
			})),
		};

		const subscriber = PaymentService.getPaymentSummary(payload).subscribe(
			({ response }) => {
				const data = (response as PaymentSummary)?.data ?? {
					paymentSummaryMonthlyItems: [],
					totalAmount: { unit: '', value: 0 },
				};

				setOrderItems(data?.paymentSummaryMonthlyItems);
				setTotalAmount(data?.totalAmount?.value?.toString());

				if (!data?.paymentSummaryMonthlyItems?.length) setError(true);
			},
			() => setError(true)
		);

		return () => subscriber.unsubscribe();
	}, []);

	if (hasError) return <Error />;

	return (
		<>
			<Card variant="elevation" className={classes.boxShadowNone}>
				<CardBody className={classes.paddingNone}>
					<div className={classes.cardDetailsContainer}>
						<HeadingM customClassName={classes.orderSummaryText}>
							{paymentMethodLabels?.orderSummary}
						</HeadingM>
					</div>
					<div className={classes.orderSummaryList}>
						{orderItems?.map((order) => (
							<div className={classes.orderSummaryItem} key={`order_item_${order?.month}_${order?.year}`}>
								<div className={classes.orderSummaryContent}>
									<BodySBold>{`${moment(order?.month, 'M')
										.locale(lang)
										.format('MMMM')} ${order?.year}`}</BodySBold>
								</div>
								<div className={classes.orderSummarySubContent}>
									{order.paymentSummaryBillDetails.map((bill) => (
										<div className={classes.orderSummaryContent} key={`bill_${bill.billNumber}`}>
											<BodyS>
												{dynamicTranslate(paymentMethodLabels?.bill_no, bill.billNumber)}
											</BodyS>
											<BodyS>
												{dynamicTranslate(
													commonLabels?.price_LTR,
													roundup(bill?.amount?.value)
												)}
											</BodyS>
										</div>
									))}
									{order.paymentSummaryBillDetails.length > 1 && (
										<div className={classes.orderSummaryContent}>
											<BodySBold>{paymentMethodLabels?.monthlySubTotal}</BodySBold>
											<BodySBold>
												{dynamicTranslate(
													commonLabels?.price_LTR,
													roundup(order?.totalAmount?.value)
												)}
											</BodySBold>
										</div>
									)}
								</div>
							</div>
						))}
						<div className={classes.orderSummaryItem}>
							<div className={classNames(classes.orderSummaryContent, classes.marginBottomNone)}>
								<HeadingMBold>{paymentMethodLabels?.totalPayable}</HeadingMBold>
								<HeadingMBold>
									{dynamicTranslate(commonLabels?.price_LTR, roundup(totalAmount || 0))}
								</HeadingMBold>
							</div>
							<div className={classes.orderSummaryContent}>
								<BodyS customClassName={classes.textRight}>{paymentMethodLabels?.inclusiveVat}</BodyS>
							</div>
						</div>
					</div>
				</CardBody>
			</Card>
			<SecurePaymentInfo />
		</>
	);
};

const SecurePaymentInfo = () => {
	const classes = useStyles();
	const paymentMethodLabels = useSelector(getPaymentMethodLabels);

	return (
		<div className={classes.securePaymentContainer}>
			<div className={classes.securePaymentHeading}>
				<img src={securePayment} />
				<BodyL customClassName={classes.securePaymentText}>{paymentMethodLabels?.yourMoneySafe}</BodyL>
			</div>
			<div>
				<DescriptionS>{paymentMethodLabels?.securePayment}</DescriptionS>
			</div>
		</div>
	);
};

const Error = () => {
	const classes = useStyles();
	const paymentStatusLabels = useSelector(getPaymentMethodLabels)?.paymentResult;

	return (
		<Card className={classes.boxShadowNone}>
			<CardContent className={classes.securePaymentContainer}>
				<img src={warning} className={classes.bottomSpace} />
				<BodyMBold disableMargin>{paymentStatusLabels?.unable_to_process_request}</BodyMBold>
				<BodyMBold>{paymentStatusLabels?.try_again_later}</BodyMBold>
			</CardContent>
		</Card>
	);
};

export default OrderSummary;
